import LinkButton from '@carvana/ds-link-button';
import { icon } from '@carvana/ds-styles';
import cx from 'classnames';
import { Info } from 'lucide-react';
import { useContext } from 'react';
import Skeleton from '@/common/helios/Skeleton';
import TransitInfo from '@/vdp/VehicleInfo/TransitInfo';
import SoftPullStatus from '~/constants/softPullStatus';
import { SectionId } from '~/constants/vdp/section';
import TransitChargeState from '~/constants/vdp/TransitChargeState';
import { useActiveAppliedTrade } from '~/context/common/TradeContext';
import { useIsDealershipPage } from '~/context/dealership/StaticContext';
import GlobalContext from '~/context/vdp/GlobalContext';
import ModalContext from '~/context/vdp/ModalContext';
import { useSectionAnalytics } from '~/context/vdp/SectionAnalyticsContext';
import UserVehicleContext, {
  useVehiclePaymentTerms
} from '~/context/vdp/UserVehicleContext';
import useIsAvailabilityDisabled from '~/hooks/vdp/selectors/useIsAvailabilityDisabled';
import useIsNotAvailableForImmediatePurchase from '~/hooks/vdp/selectors/useIsNotAvailableForImmediatePurchase';
import useSoftPullStatus from '~/hooks/vdp/selectors/useSoftPullStatus';
import useTransitChargeInfo from '~/hooks/vdp/selectors/useTransitChargeInfo';
import useIsHighNetTrade from '~/hooks/vdp/useIsHighNetTrade';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import useCanShowTerms from './hooks/useCanShowTerms';
import { Container, DetailText, Separator } from './Terms.styles';
import useIsInZipExperiment from '../useIsInZipExperiment';

function CashDown({ hasSoftPull }: { hasSoftPull: boolean }) {
  const isAvailabilityDisabled = useIsAvailabilityDisabled();
  const transitCharge = useTransitChargeInfo();
  const isAvailableForImmediatePurchase =
    !useIsNotAvailableForImmediatePurchase();
  const isInZipExperiment = useIsInZipExperiment();
  const isDealership = useIsDealershipPage();

  const isTransitChargeLoading =
    isAvailableForImmediatePurchase && !transitCharge.isLoaded;
  const hasTransitCharge =
    transitCharge.state !== TransitChargeState.Unknown ||
    isTransitChargeLoading;

  const showTransitInfo =
    !isAvailabilityDisabled &&
    hasTransitCharge &&
    isInZipExperiment &&
    !isDealership;

  const isHighNetTrade = useIsHighNetTrade();
  const { downPaymentDetails } = useVehiclePaymentTerms();
  const { additionalCashDown = 0 } = downPaymentDetails ?? {};

  const showCashDown = !isHighNetTrade;
  const showSeparator = showCashDown && showTransitInfo;

  return showTransitInfo || showCashDown ? (
    <div className="flex items-center gap-sm">
      {showCashDown ? (
        <DetailText data-testid="vehicle-info-card-cash-down">
          {showCashDown && (
            <span
              className={cx({
                'text-text-success t-label-sm':
                  additionalCashDown === 0 && hasSoftPull
              })}
            >
              ${formatAndDropDecimal(additionalCashDown)} cash down
            </span>
          )}
        </DetailText>
      ) : null}
      {showTransitInfo && (
        <DetailText>
          {showSeparator && <Separator className="bg-icon-default" />}
          <TransitInfo transitCharge={transitCharge} />
        </DetailText>
      )}
    </div>
  ) : null;
}

function TermsContent({
  hasSoftPull,
  hasExpiredSoftPull
}: {
  hasSoftPull: boolean;
  hasExpiredSoftPull: boolean;
}) {
  const activeAppliedTrade = useActiveAppliedTrade();
  const { setLazyLoadEnabled } = useContext(GlobalContext);
  const { setSoftPullModalOpen } = useContext(ModalContext);
  const {
    vehiclePricingInfo: { isLoaded, isLoading }
  } = useContext(UserVehicleContext);
  const isHighNetTrade = useIsHighNetTrade();
  const { analyticsAttributes, analyticsId } = useSectionAnalytics();
  const { monthlyPayment = 0 } = useVehiclePaymentTerms();

  const isTradeInAppliedAndActive = Boolean(activeAppliedTrade);

  if (!isLoaded || isLoading) {
    return (
      <div className="flex flex-col gap-xs">
        <Skeleton className="my-sm" height={16} />
        <Skeleton className="my-sm" height={16} />
      </div>
    );
  }

  return (
    <div className="flex flex-row flex-wrap gap-x-xl gap-y-lg">
      {hasExpiredSoftPull && (
        <div className="hidden w-full flex-wrap justify-between gap-x-md sm:flex">
          <p className="text-text-strong t-strong-sm">Your terms expired</p>
          <LinkButton
            data-adobe-id="refresh-your-terms"
            data-analytics-id={analyticsId}
            data-analytics-attributes={JSON.stringify({
              ...analyticsAttributes,
              description: 'soft pull',
              location: 'expired terms',
              text: 'Refresh your terms'
            })}
            data-testid="refresh-my-terms-link"
            onClick={() => setSoftPullModalOpen(true)}
            size="sm"
            underline={false}
          >
            Refresh your terms
          </LinkButton>
        </div>
      )}
      <div className="flex flex-col gap-xs">
        {!isHighNetTrade && (
          <DetailText
            data-testid="vehicle-info-card-monthly-payment"
            className="flex items-center gap-sm"
          >
            <div
              className={cx(
                'text-text-default',
                hasSoftPull ? 't-strong-sm' : 't-body-sm'
              )}
              data-testid="vehicle-info-card-monthly-payment-text"
            >
              ${monthlyPayment.toLocaleString()}/mo
            </div>
            {!hasSoftPull && (
              <span className="text-text-default t-body-sm">estimated</span>
            )}
            {isTradeInAppliedAndActive && (
              <span className="text-text-default t-body-sm">w/ trade-in</span>
            )}
            <a
              className="flex items-center"
              data-adobe-id="price-info"
              data-analytics-id={analyticsId}
              data-analytics-attributes={JSON.stringify({
                ...analyticsAttributes,
                description: 'price info'
              })}
              data-testid="price-info-link"
              href={`#${SectionId.PriceDetails}`}
              onClick={() => {
                setLazyLoadEnabled(false);
              }}
            >
              <Info
                data-testid="vehicle-info-card-terms-info-button"
                color={icon.weak}
                size={16}
              />
            </a>
          </DetailText>
        )}

        <CashDown hasSoftPull={hasSoftPull} />
      </div>
    </div>
  );
}

export default function Terms() {
  const canShowTerms = useCanShowTerms();
  const { softPullStatus } = useSoftPullStatus();

  const hasSoftPull = softPullStatus === SoftPullStatus.Active;
  const hasExpiredSoftPull = softPullStatus === SoftPullStatus.Expired;

  return canShowTerms ? (
    <Container hasExpiredSoftPull={hasExpiredSoftPull}>
      <TermsContent
        hasExpiredSoftPull={hasExpiredSoftPull}
        hasSoftPull={hasSoftPull}
      />
    </Container>
  ) : null;
}
